import Immutable from 'immutable';
import gsap from 'gsap';
import { defaultMemoize, createSelectorCreator, createSelector } from 'reselect';
import {
  aggregateStyle,
  getSelectedAdstate,
  makeSortKeyframes,
  makeGetComponentsKeyframes,
  makeGetSelectedComponentsKeyframes,
  makeGetActiveGroupKeyframes,
  getActiveGroup,
} from 'Selectors';

const createDeepSelector = createSelectorCreator(defaultMemoize, Immutable.is);

export const makeTweenStyle = () =>
  createSelector(
    state => state.first,
    state => state.next,
    state => state.currentTimeAsMilliseconds,
    (thefirst, thenext, currentTimeAsMilliseconds) => {
      const style = thefirst.style.toObject();
      const nextStyle = thenext.style.toObject();

      // add any missing properties from the next keyframe to the previous one
      Object.keys(nextStyle).forEach((key) => {
        if (typeof style[key] === 'undefined') {
          style[key] = nextStyle[key];
        }

        // gsap (TweenLite.to) will use the ease property of `nextStyle`.
        // in order to show the right easing, we copy the ease into `style`.
        style.ease = nextStyle.ease;
      });

      if (style.scale) style.scale = Math.max(0.0001, style.scale);

      gsap.to(style, thenext.t - thefirst.t, nextStyle)
        .seek((currentTimeAsMilliseconds / 1000) - thefirst.t, false)
        .pause();

      style.transformOrigin = nextStyle.transformOrigin ? nextStyle.transformOrigin : '50% 50%';

      return style;
    },
  );

export const makeKeyframeStyleAtTime = () => {
  const sortKeyframes = makeSortKeyframes();
  const tweenStyle = makeTweenStyle();
  const keyframeStyleAtTime = (keyframes, currentTime) => {
    let style = {};

    if (keyframes.count() === 0) {
      return style;
    }

    const sortedKeyframes = sortKeyframes(keyframes);

    // if there's a keyframe at time - use it unchanged
    const keyframe = sortedKeyframes.find(kf => kf.t === currentTime);
    if (keyframe) {
      style = keyframe.style.toObject();
    } else {
      let first = sortedKeyframes.filter(kf => kf.t < currentTime).last();
      const next = sortedKeyframes.find(kf => kf.t > currentTime);

      // if this isn't the last keyframe apply a tween
      if (next) {
        // TODO: replace cheap hack for dealing with -1s keyframe phantom animations
        if (first === undefined) { first = next; }
        if (first.t === -1) { first = next; }

        style = tweenStyle({
          first,
          next,
          currentTimeAsMilliseconds: Math.round(currentTime * 1000),
        });

        delete style._gsTweenID;// eslint-disable-line no-underscore-dangle
      } else {
        style = first.style.toObject();
      }
    }

    return style;
  };

  return keyframeStyleAtTime;
};

export const makeCalculateStyle = () => {
  const getComponentsKeyframes = makeGetComponentsKeyframes();
  const keyframeStyleAtTime = makeKeyframeStyleAtTime();
  return createDeepSelector(
    (state, props) => getComponentsKeyframes(state, props),
    (state, props) => props.currentTime,
    (keyframes, currentTime) => keyframeStyleAtTime(keyframes, currentTime),
  );
};

export const makeCalculateSelectionStyle = () => {
  const getSelectedComponentsKeyframes = makeGetSelectedComponentsKeyframes();
  const keyframeStyleAtTime = makeKeyframeStyleAtTime();

  return createDeepSelector(
    (state, props) => getSelectedComponentsKeyframes(state, props),
    state => getSelectedAdstate(state).currentTime,
    (keyframesGroupedByComponent, currentTime) => {
      const selectedKeyframes = keyframesGroupedByComponent
        .flatten(true)
        .filter(m => m.selected);
      if (selectedKeyframes.count() > 0) {
        return aggregateStyle(
          selectedKeyframes.map(keyframe => keyframe.style.toJS()),
        );
      }
      return aggregateStyle(
        keyframesGroupedByComponent.map(keyframes =>
          keyframeStyleAtTime(keyframes, currentTime),
        ),
      );
    },
  );
};

export const makeGetActiveGroupBoundingRect = () => {
  const keyframeStyleAtTime = makeKeyframeStyleAtTime();
  const getActiveGroupKeyframes = makeGetActiveGroupKeyframes();
  return createSelector(
    state => getActiveGroup(state),
    state => getActiveGroupKeyframes(state),
    state => getSelectedAdstate(state).currentTime,
    (group, keyframes, currentTime) => {
      const rect = { left: 0, top: 0, width: 0, height: 0 };
      keyframes.map((keyframe) => {
        const style = keyframeStyleAtTime(keyframe, currentTime);
        rect.left += style.left;
        rect.top += style.top;
        rect.width += style.width;
        rect.height += style.height;
      });
      return rect;
    },
  );
};

export const makeGetSelectedComponentsBoundingRect = () => {
  const getSelectedComponentsKeyframes = makeGetSelectedComponentsKeyframes();
  const keyframeStyleAtTime = makeKeyframeStyleAtTime();

  return createSelector(
    state => getSelectedComponentsKeyframes(state),
    state => getSelectedAdstate(state).currentTime,
    (keyframesGroupedByComponent, currentTime) => {
      const bounds = keyframesGroupedByComponent.map((keyframes) => {
        const style = keyframeStyleAtTime(keyframes, currentTime);
        const { left = 0, top = 0, width = 0, height = 0 } = style;

        return {
          left,
          top,
          right: left + width,
          bottom: top + height,
        };
      });

      const overallBounds = {
        left: Math.min(...bounds.map(b => b.left)),
        top: Math.min(...bounds.map(b => b.top)),
        bottom: Math.max(...bounds.map(b => b.bottom)),
        right: Math.max(...bounds.map(b => b.right)),
      };

      return {
        left: overallBounds.left,
        top: overallBounds.top,
        width: overallBounds.right - overallBounds.left,
        height: overallBounds.bottom - overallBounds.top,
      };
    },
  );
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/selectors/animation.js